import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import useModules from "../hooks/useModules";
import ModuleContextChip from "../components/ModuleContextChip";
import ProjectPicker from "../components/ProjectPicker";
import { api, formatApiError } from "../lib/api";
import { useHasPermission } from "../contexts/PermissionsContext";
import { formatDate, statusBadgeClass } from "../lib/domain";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Plus, Coins } from "lucide-react";
import { toast } from "sonner";
import useFilterUrlSync from "../hooks/useFilterUrlSync";
import { usePageAction } from "../contexts/UXChromeContext";
import { TableSkeleton } from "../components/SkeletonHelpers";
import EmptyState from "../components/EmptyState";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import DeductionBreakdown from "../components/DeductionBreakdown";
import DateInput from "../components/DateInput";

const ALL = "__all__";
const STATUSES = ["draft", "prepared", "dept_confirmed", "finance_reviewed", "approved", "funded", "paid", "closed", "cancelled"];
const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Payroll() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { modules } = useModules();
    const scopedId = useScopedModuleId();
    const canCreate = useHasPermission("payroll.create_period", scopedId || null);
    const [periods, setPeriods] = useState([]);
    // Iter70 — Filter defaults shared between initial state, URL sync, and the
    // Clear-filters reset. Anything matching a default gets stripped from the URL.
    const FILTER_DEFAULTS = useMemo(() => ({
        module_id: scopedId || ALL,
        status: ALL,
        period_type: ALL,
        project_id: ALL,
    }), [scopedId]);
    const [filters, setFilters] = useState(FILTER_DEFAULTS);
    const [loading, setLoading] = useState(true);
    const [openNew, setOpenNew] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [projects, setProjects] = useState([]);
    // period_id -> project_cost when a project filter is applied
    const [periodProjectCost, setPeriodProjectCost] = useState({});

    // Iter70 — Two-way URL sync for the Payroll filter bar. Debounced so
    // filter clicks don't spam history entries. Back/Forward and direct
    // bookmark links now restore filter state exactly.
    // Sentinel translation: internal state uses ALL = "__all__"; the URL
    // uses the friendly literal "all" (spec compliance).
    const _allCodec = {
        toUrl: (v) => (v === ALL ? "" : v),
        fromUrl: (raw) => (raw === "all" || raw === "" ? ALL : raw),
    };
    useFilterUrlSync({
        filters: { ...filters, show_deleted: showDeleted },
        defaults: { ...FILTER_DEFAULTS, show_deleted: false },
        paramMap: {
            module_id: { param: "module_id", ..._allCodec },
            status: { param: "status", ..._allCodec },
            period_type: { param: "period_type", ..._allCodec },
            project_id: { param: "project", ..._allCodec },
            show_deleted: "show_deleted",
        },
        hydrate: (patch) => {
            const { show_deleted, ...rest } = patch;
            if (Object.keys(rest).length > 0) setFilters((f) => ({ ...f, ...rest }));
            if (show_deleted !== undefined) setShowDeleted(!!show_deleted);
        },
    });

    const clearAllFilters = React.useCallback(() => {
        setFilters(FILTER_DEFAULTS);
        setShowDeleted(false);
    }, [FILTER_DEFAULTS]);

    usePageAction(
        canCreate ? { label: "New Payroll Period", run: () => setOpenNew(true) } : null,
        [canCreate],
    );

    const load = async () => {
        setLoading(true);
        const params = { page_size: 100 };
        if (filters.module_id !== ALL) params.module_id = filters.module_id;
        if (filters.status !== ALL) params.status = filters.status;
        if (filters.period_type !== ALL) params.period_type = filters.period_type;
        if (showDeleted) params.include_deleted = true;
        try {
            const { data } = await api.get("/payroll/periods", { params });
            setPeriods(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [filters.module_id, filters.status, filters.period_type, showDeleted]);

    // Load projects list for the module-scoped filter
    useEffect(() => {
        if (!scopedId) { setProjects([]); return; }
        api.get("/projects", { params: { module_id: scopedId } })
            .then(({ data }) => setProjects(Array.isArray(data) ? data : (data.items || [])))
            .catch(() => setProjects([]));
    }, [scopedId]);

    // When a project filter is active, enrich each period with its cost for that project.
    useEffect(() => {
        if (filters.project_id === ALL || periods.length === 0) {
            setPeriodProjectCost({});
            return;
        }
        let alive = true;
        Promise.all(periods.map(async (p) => {
            try {
                const { data } = await api.get(`/payroll/periods/${p.id}/cost-by-project`);
                const hit = (data.by_project || []).find((r) => r.project_id === filters.project_id);
                return [p.id, hit ? hit.cost : 0];
            } catch {
                return [p.id, 0];
            }
        })).then((entries) => alive && setPeriodProjectCost(Object.fromEntries(entries)));
        return () => { alive = false; };
    }, [filters.project_id, periods]);

    const modById = Object.fromEntries(modules.map((m) => [m.id, m]));
    const visiblePeriods = filters.project_id === ALL
        ? periods
        : periods.filter((p) => (periodProjectCost[p.id] || 0) > 0);

    return (
        <AppShell title="Payroll Periods">
            <NewPeriodDialog open={openNew} onOpenChange={setOpenNew} onSaved={load} modules={modules} />
            <PreviewPayrollDialog open={previewOpen} onOpenChange={setPreviewOpen} moduleId={scopedId} />
            <section className="space-y-5" data-testid="payroll-page">
                <div className="asl-card p-4 flex flex-wrap items-end gap-3">
                    {/* Iter60.p13 — hide Module dropdown when inside a workspace. */}
                    {scopedId ? (
                        <ModuleContextChip moduleId={scopedId} testid="pay-module-chip" />
                    ) : (
                        <FieldSel label="Module" value={filters.module_id}
                            onChange={(v) => setFilters((f) => ({ ...f, module_id: v }))}
                            items={[{ value: ALL, label: "All modules" }, ...modules.map((m) => ({ value: m.id, label: m.name }))]}
                            testid="pay-module-filter" />
                    )}
                    <FieldSel label="Status" value={filters.status}
                        onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                        items={[{ value: ALL, label: "All" }, ...STATUSES.map((s) => ({ value: s, label: s }))]}
                        testid="pay-status-filter" />
                    <FieldSel label="Type" value={filters.period_type}
                        onChange={(v) => setFilters((f) => ({ ...f, period_type: v }))}
                        items={[{ value: ALL, label: "All" },
                                { value: "weekly", label: "Weekly" },
                                { value: "bi_weekly", label: "Bi-weekly" },
                                { value: "monthly", label: "Monthly" },
                                { value: "custom", label: "Custom" }]}
                        testid="pay-type-filter" />
                    {scopedId && projects.length > 0 && (
                        <div className="flex flex-col gap-1 min-w-[200px]">
                            <label className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                                Project
                            </label>
                            {/* Iter67 F1 — Universal ProjectPicker. */}
                            <ProjectPicker
                                projects={projects}
                                value={filters.project_id === ALL ? "" : filters.project_id}
                                onValueChange={(v) => setFilters((f) => ({ ...f, project_id: v || ALL }))}
                                allowNone
                                noneLabel="— All projects —"
                                placeholder="— All projects —"
                                testid="pay-project-filter"
                            />
                        </div>
                    )}
                    <div className="ml-auto flex items-center gap-3">
                        {/* Iter70 — Clear filters resets every managed filter
                            and lets Back navigation restore the caller's
                            previous state cleanly. */}
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={clearAllFilters}
                            data-testid="pay-clear-filters"
                            title="Reset all filters to their defaults"
                        >
                            Clear filters
                        </Button>
                        <ShowDeletedToggle value={showDeleted} onChange={setShowDeleted} testid="pay-show-deleted" />
                        {scopedId && (
                            <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)} data-testid="pay-preview-button">
                                Preview
                            </Button>
                        )}
                        {canCreate && (
                            <Button size="sm" className="asl-navy-bg text-white" onClick={() => setOpenNew(true)} data-testid="pay-new-button">
                                <Plus className="h-4 w-4 mr-1" /> New Period
                            </Button>
                        )}
                    </div>
                </div>

                <div className="asl-card overflow-hidden">
                    {loading ? <TableSkeleton rows={6} cols={9} testid="pay-loading" />
                    : visiblePeriods.length === 0 ? (() => {
                        const isFilt = filters.module_id !== ALL || filters.status !== ALL || filters.period_type !== ALL || filters.project_id !== ALL;
                        return (
                            <EmptyState
                                icon={Coins}
                                title={isFilt ? "Nothing matches these filters. Try widening the range or clearing a filter." : "No payroll periods yet"}
                                description={isFilt ? "Adjust filters or clear them." : "Create a period to compute wages, overtime, and management fees."}
                                ctaLabel={canCreate && !isFilt ? "New Payroll Period" : undefined}
                                onCta={canCreate && !isFilt ? () => setOpenNew(true) : undefined}
                                filtered={isFilt}
                                onClear={() => setFilters({ module_id: scopedId || ALL, status: ALL, period_type: ALL, project_id: ALL })}
                                testid="pay-empty"
                            />
                        );
                    })()
                    : <table className="asl-table asl-table--zebra" data-testid="pay-table">
                        <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                            style={{ background: "var(--asl-navy-50)", borderBottom: "1px solid var(--asl-border)" }}>
                            <tr>
                                <th className="text-left px-3 py-3">Name</th>
                                <th className="text-left px-3 py-3">Module</th>
                                <th className="text-left px-3 py-3">Type</th>
                                <th className="text-left px-3 py-3">Scope</th>
                                <th className="text-left px-3 py-3">Range</th>
                                <th className="text-right px-3 py-3">Gross</th>
                                <th className="text-right px-3 py-3">Net</th>
                                <th className="text-right px-3 py-3">Funding Req</th>
                                {filters.project_id !== ALL && <th className="text-right px-3 py-3">Project Cost</th>}
                                <th className="text-left px-3 py-3">Status</th>
                                <th className="text-right px-3 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visiblePeriods.map((p) => (
                                <PayrollPeriodRow key={p.id} p={p} modById={modById} navigate={navigate}
                                    onChange={load}
                                    projectFilter={filters.project_id !== ALL ? { cost: periodProjectCost[p.id] || 0 } : null} />
                            ))}
                        </tbody>
                    </table>}
                </div>
            </section>
        </AppShell>
    );
}

function NewPeriodDialog({ open, onOpenChange, onSaved, modules }) {
    const [form, setForm] = useState({
        name: "", module_id: "", period_type: "weekly",
        worker_category_scope: "casual",
        start_date: "", end_date: "", notes: "",
    });
    // Iter57 — auto-suggest hint from /api/payroll/periods/suggest.
    // Kept in a ref so the effect below stays stable across renders.
    const [hint, setHint] = useState(null);
    // Track whether the user has hand-edited the dates so we don't clobber their input.
    const editedRef = React.useRef({ start: false, end: false });

    // Reset the edited-flags whenever the dialog reopens.
    React.useEffect(() => {
        if (open) editedRef.current = { start: false, end: false };
    }, [open]);

    // Fetch suggest whenever module or type changes (and dialog is open).
    React.useEffect(() => {
        if (!open || !form.module_id || !form.period_type) { setHint(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/payroll/periods/suggest", {
                    params: { module_id: form.module_id, type: form.period_type },
                });
                if (cancelled) return;
                setHint(data);
                // Only auto-fill fields the user hasn't manually edited.
                setForm((f) => ({
                    ...f,
                    start_date: editedRef.current.start ? f.start_date : data.suggested_start,
                    end_date: editedRef.current.end ? f.end_date : data.suggested_end,
                }));
            } catch {
                if (!cancelled) setHint(null);
            }
        })();
        return () => { cancelled = true; };
    }, [open, form.module_id, form.period_type]);

    // Smart-default NAME (uses whatever start_date/end_date currently are, whether
    // suggested or manually edited).
    React.useEffect(() => {
        if (!open || !form.start_date || !form.end_date || !form.module_id) return;
        const start = new Date(form.start_date + "T00:00:00");
        const end = new Date(form.end_date + "T00:00:00");
        if (isNaN(start) || isNaN(end)) return;
        const monthLabel = (d) => d.toLocaleString("en-US", { month: "short", day: "2-digit" });
        const mod = modules.find((m) => m.id === form.module_id);
        const scopeLabel = ({ casual: "Casual", non_casual: "Non-Casual", all: "All" })[form.worker_category_scope] || "All";
        const typeLabel = ({ weekly: "Weekly", bi_weekly: "Bi-Weekly", monthly: "Monthly", daily: "Daily", custom: "Custom" })[form.period_type] || form.period_type;
        const name = mod
            ? `${mod.name} — ${scopeLabel} ${typeLabel} · ${monthLabel(start)}–${monthLabel(end)}, ${end.getFullYear()}`
            : "";
        setForm((f) => (f.name === name ? f : { ...f, name }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, form.start_date, form.end_date, form.module_id, form.worker_category_scope, form.period_type, modules]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/payroll/periods", form);
            toast.success("Period created");
            onOpenChange(false);
            onSaved?.(data);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="pay-new-dialog">
                <DialogHeader><DialogTitle>New Payroll Period</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <Field label="Name *"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pay-new-name" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Module *">
                            <Select value={form.module_id} onValueChange={(v) => setForm({ ...form, module_id: v })}>
                                <SelectTrigger data-testid="pay-new-module"><SelectValue placeholder="Choose…" /></SelectTrigger>
                                <SelectContent>{modules.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        <Field label="Type *">
                            <Select value={form.period_type} onValueChange={(v) => setForm({ ...form, period_type: v })}>
                                <SelectTrigger data-testid="pay-new-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {/* "daily" is supported by the suggest endpoint but the
                                        Period creation validator only accepts these 4 types.
                                        Adding "daily" would send users to a 400 wall. */}
                                    {["weekly", "bi_weekly", "monthly", "custom"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Scope *">
                            <Select value={form.worker_category_scope} onValueChange={(v) => setForm({ ...form, worker_category_scope: v })}>
                                <SelectTrigger data-testid="pay-new-scope"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {["casual", "non_casual", "all"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Start *"><DateInput required value={form.start_date} onChange={(v) => { editedRef.current.start = true; setForm({ ...form, start_date: v }); }} data-testid="pay-new-start" /></Field>
                        <Field label="End *"><DateInput required value={form.end_date} onChange={(v) => { editedRef.current.end = true; setForm({ ...form, end_date: v }); }} data-testid="pay-new-end" /></Field>
                    </div>
                    {hint && (
                        <div
                            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900"
                            data-testid="pay-new-hint"
                        >
                            {hint.attendance_range?.earliest ? (
                                <>
                                    <span data-testid="pay-new-hint-range">
                                        Attendance data available from <strong>{hint.attendance_range.earliest}</strong>
                                        {" to "}<strong>{hint.attendance_range.latest}</strong>.
                                    </span>{" "}
                                </>
                            ) : (
                                <span data-testid="pay-new-hint-range">No attendance data yet.{" "}</span>
                            )}
                            <span data-testid="pay-new-hint-reason">
                                Suggested: <strong>{hint.suggested_start}</strong> → <strong>{hint.suggested_end}</strong>
                                {" ("}{hint.reason}{")"}.
                            </span>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" className="asl-navy-bg text-white" data-testid="pay-new-submit">Create</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return <div className="space-y-1"><Label className="text-xs uppercase">{label}</Label>{children}</div>;
}
function FieldSel({ label, value, onChange, items, testid }) {
    return <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="w-44" data-testid={testid}><SelectValue /></SelectTrigger>
            <SelectContent>{items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
        </Select>
    </div>;
}


function PayrollPeriodRow({ p, modById, navigate, onChange, projectFilter }) {
    const t = p.totals || {};
    const canHard = ["draft", "prepared"].includes(p.status);
    // Iter59.p0 — Route rows to the module-scoped URL directly instead of
    // /payroll/periods/{id} (which relies on `LegacyRedirect` and used to race
    // with the modules-list fetch and dump the user home). Fallback keeps the
    // deep-link path alive for cases where module lookup fails.
    const modCode = modById[p.module_id]?.code;
    const targetHref = modCode
        ? `/m/${String(modCode).toLowerCase()}/payroll/${p.id}`
        : `/payroll/periods/${p.id}`;
    // Iter70 (Fix B) — Row body no longer navigates. Only the period Name
    // is clickable so accidental clicks on data cells (Module/Type/Range/
    // Gross/Net…) don't drop the user's filters.
    return (
        <tr
            data-testid={`pay-row-${p.id}`}
            className="border-t"
            style={{ borderColor: "var(--asl-border)" }}
        >
            <td className="px-3 py-3 font-medium asl-navy-text">
                <DeletedRowDecorator deleted={!!p.deleted_at}>
                    <Link
                        to={targetHref}
                        className="hover:underline focus:underline focus:outline-none"
                        title="Open period"
                        data-testid={`pay-name-link-${p.id}`}
                    >
                        {p.name}
                    </Link>
                </DeletedRowDecorator>
                {p.deleted_at && <DeletedPill deletedAt={p.deleted_at} />}
            </td>
            <td className="px-3 py-3 text-xs">{modById[p.module_id]?.code}</td>
            <td className="px-3 py-3 text-xs">{p.period_type}</td>
            <td className="px-3 py-3 text-xs">{p.worker_category_scope}</td>
            <td className="px-3 py-3 mono text-xs">{formatDate(p.start_date)} → {formatDate(p.end_date)}</td>
            <td className="px-3 py-3 mono text-xs text-right">{fmtUSD(t.gross_total)}</td>
            <td className="px-3 py-3 mono text-xs text-right asl-navy-text font-semibold">{fmtUSD(t.net_payable_total)}</td>
            <td className="px-3 py-3 mono text-xs text-right">{fmtUSD(t.funding_required_total)}</td>
            {projectFilter && (
                <td className="px-3 py-3 mono text-xs text-right asl-crimson-text font-semibold" data-testid={`pay-proj-cost-${p.id}`}>
                    {fmtUSD(projectFilter.cost)}
                </td>
            )}
            <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(p.status)}`}>{p.status}</span></td>
            <td className="px-3 py-3 text-right" data-no-row-click>
                <RowActions
                    entity="payroll"
                    endpoint="/payroll/periods"
                    id={p.id}
                    label={p.name}
                    deleted={!!p.deleted_at}
                    moduleId={p.module_id}
                    hardDeleteSupported={canHard}
                    disabledHardReason={!canHard ? `Allowed only on draft/prepared periods (current: ${p.status})` : null}
                    onChange={onChange}
                    testid={`pay-actions-${p.id}`}
                />
            </td>
        </tr>
    );
}


// Iter53 — Preview payroll (compute-without-persist) for a date range.
function PreviewPayrollDialog({ open, onOpenChange, moduleId }) {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const [from, setFrom] = useState(`${y}-${m}-01`);
    const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState(null);

    const run = async () => {
        if (!moduleId) { toast.error("Open Payroll under a specific module"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/payroll/preview", {
                module_id: moduleId, date_from: from, date_to: to,
                worker_category_scope: "all",
            });
            setPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    const totals = preview?.totals || {};
    const lines = preview?.lines || [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl" data-testid="payroll-preview-dialog">
                <DialogHeader>
                    <DialogTitle>Preview payroll</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="flex gap-3 items-end">
                        <div>
                            <Label>From</Label>
                            <DateInput value={from} onChange={setFrom} testid="preview-from" />
                        </div>
                        <div>
                            <Label>To</Label>
                            <DateInput value={to} onChange={setTo} testid="preview-to" />
                        </div>
                        <Button className="asl-navy-bg text-white" onClick={run} disabled={loading} data-testid="preview-run-btn">
                            {loading ? "Computing…" : "Compute preview"}
                        </Button>
                    </div>
                    {preview && (
                        <div className="border rounded-md overflow-hidden max-h-96 overflow-y-auto" data-testid="preview-result">
                            <div className="bg-slate-50 border-b px-3 py-2 flex flex-wrap gap-4 text-xs">
                                <span><strong>{totals.worker_count}</strong> employees</span>
                                <span>Base: <span className="mono">{fmtUSD(totals.base_pay_total)}</span></span>
                                <span>Overtime: <span className="mono">{fmtUSD(totals.overtime_pay_total)}</span></span>
                                <span>Gross: <span className="mono">{fmtUSD(totals.gross_pay_total)}</span></span>
                                <span className="text-rose-700">Deductions: <span className="mono">{fmtUSD(totals.deductions_total)}</span></span>
                                <span className="asl-navy-text font-semibold">Net: <span className="mono">{fmtUSD(totals.net_pay_total)}</span></span>
                                {/* Iter60.p26 — total Mgmt Fee for the previewed range. */}
                                <span className="text-slate-600" data-testid="preview-mgmt-fee-total">
                                    Mgmt Fee: <span className="mono">{fmtUSD(totals.management_fee_total)}</span>
                                </span>
                            </div>
                            <table className="w-full text-xs">
                                <thead className="text-[10px] uppercase tracking-wider bg-slate-50 border-b">
                                    <tr>
                                        <th className="text-left px-3 py-1.5">Employee</th>
                                        <th className="text-left px-3 py-1.5">Freq</th>
                                        <th className="text-right px-3 py-1.5">Base</th>
                                        <th className="text-right px-3 py-1.5">OT</th>
                                        <th className="text-right px-3 py-1.5 text-rose-700">Deductions</th>
                                        <th className="text-right px-3 py-1.5">Net</th>
                                        <th
                                            className="text-right px-3 py-1.5"
                                            data-testid="payroll-lines-mgmt-fee-header"
                                        >
                                            Mgmt Fee
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((l) => (
                                        <tr key={l.id} className="border-t" data-testid={`preview-row-${l.employee_code}`}>
                                            <td className="px-3 py-1.5">
                                                {l.employee_name}
                                                {l.module_employee_id && (
                                                    <span className="mono text-[10px] asl-crimson-text ml-1">({l.module_employee_id})</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-1.5">{l.wage_rate_snapshot?.payment_frequency}</td>
                                            <td className="px-3 py-1.5 mono text-right">{fmtUSD(l.base_pay)}</td>
                                            <td className="px-3 py-1.5 mono text-right">{fmtUSD(l.overtime_pay)}</td>
                                            <td className="px-3 py-1.5 mono text-right">
                                                <DeductionBreakdown
                                                    lineId={l.employee_id || l.employee_code}
                                                    total={l.deductions_total}
                                                    deductions={l.deductions}
                                                />
                                            </td>
                                            <td className="px-3 py-1.5 mono text-right font-semibold asl-navy-text">{fmtUSD(l.net_pay)}</td>
                                            <td
                                                className="px-3 py-1.5 mono text-right text-slate-600"
                                                data-testid={`payroll-line-mgmt-fee-${l.employee_id || l.employee_code}`}
                                            >
                                                {fmtUSD(l.management_fee)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {preview.warnings?.length > 0 && (
                                <div className="border-t bg-amber-50 px-3 py-2 text-[11px] text-amber-900" data-testid="preview-warnings">
                                    {preview.warnings.slice(0, 5).map((w, i) => <div key={i}>{w}</div>)}
                                    {preview.warnings.length > 5 && <div className="mt-1">…and {preview.warnings.length - 5} more</div>}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
